import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Carte, Chargement, Erreur, Etiquette, Montant, Nombre, Vide } from '../../composants/communs.tsx';
import {
  BoutonImprimer,
  exporterTableauPdf,
  formaterFcfa,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import {
  debutDuMois,
  type LigneArticleResultat,
  type LigneResultat,
  type Resultats as Reponse,
  type ResultatsParArticle,
} from './types.ts';

/** « Du 1 sept. 2026 au 23 sept. 2026 » — sous-titre partagé par les exports PDF des deux onglets. */
function sousTitrePeriode(depuis: string, jusquA: string): string {
  const formater = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  return `Du ${formater(depuis)} au ${formater(jusquA)}`;
}

/**
 * Champs de période partagés entre les deux onglets — même plage de dates
 * des deux côtés, pour comparer le global et le détail sans les décaler.
 */
function ChampsPeriode({
  depuis,
  jusquA,
  onDepuis,
  onJusquA,
}: {
  depuis: string;
  jusquA: string;
  onDepuis: (valeur: string) => void;
  onJusquA: (valeur: string) => void;
}): ReactNode {
  return (
    <div className="ligne">
      <label>
        <span className="visuellement-cache">Du</span>
        <input
          type="date"
          aria-label="Du"
          max={jusquA}
          value={depuis}
          onChange={(evenement) => {
            onDepuis(evenement.target.value);
          }}
        />
      </label>
      <label>
        <span className="visuellement-cache">Au</span>
        <input
          type="date"
          aria-label="Au"
          min={depuis}
          value={jusquA}
          onChange={(evenement) => {
            onJusquA(evenement.target.value);
          }}
        />
      </label>
    </div>
  );
}

/**
 * Résultat global — une ligne par boutique, comparaison, pas consolidation.
 *
 * Le tableau de bord répond à « combien on a fait » ; cet écran répond à
 * « laquelle marche, et laquelle décroche ». Une ligne par boutique, dans
 * l'ordre des codes, y compris celles à zéro : une boutique absente du
 * tableau passerait pour oubliée, alors qu'un zéro est ce qui alerte.
 *
 * La ligne Total est affichée en DERNIER, jamais en tête : elle se lit après
 * les boutiques, sinon l'œil s'y arrête et ne compare plus rien.
 */
export function Resultats(): ReactNode {
  const { boutiqueId } = useSession();
  const { ouvrir } = useApercuPdf();
  const [depuis, setDepuis] = useState(debutDuMois);
  const [jusquA, setJusquA] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isPending, error } = useQuery({
    queryKey: ['resultats', boutiqueId, depuis, jusquA],
    queryFn: () =>
      appeler<Reponse>(
        `/resultats?depuis=${depuis}&jusqu_a=${jusquA}` +
          (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`),
      ),
  });

  const lignes = data?.lignes ?? [];
  const avecMarge = lignes.some((ligne) => ligne.marge !== undefined);
  const avecStock = lignes.some((ligne) => ligne.valeur_stock !== undefined);

  const colonnesPdf: Colonne<LigneResultat>[] = [
    { cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (l) => `${l.code} ${l.nom}` },
    { cle: 'ventes', entete: 'Ventes', numerique: true, rendu: () => null, texte: (l) => String(l.ventes) },
    { cle: 'ca', entete: 'Chiffre d’affaires', numerique: true, rendu: () => null, texte: (l) => formaterFcfa(l.chiffre_affaires) },
    { cle: 'panier', entete: 'Panier moyen', numerique: true, rendu: () => null, texte: (l) => formaterFcfa(l.panier_moyen) },
    ...(avecMarge
      ? [
          {
            cle: 'marge',
            entete: 'Marge',
            numerique: true,
            rendu: () => null,
            texte: (l: LigneResultat) => (l.marge === undefined ? '—' : formaterFcfa(l.marge)),
          },
          {
            cle: 'taux',
            entete: 'Taux',
            numerique: true,
            rendu: () => null,
            texte: (l: LigneResultat) => (l.taux_marge_pct === undefined ? '—' : `${String(l.taux_marge_pct)} %`),
          },
        ]
      : []),
    {
      cle: 'annulations',
      entete: 'Annulations',
      numerique: true,
      rendu: () => null,
      texte: (l) => (l.contrepassations === 0 ? '0' : `${String(l.contrepassations)} · ${formaterFcfa(l.montant_contrepasse)}`),
    },
    {
      cle: 'ecart',
      entete: 'Écart de caisse',
      numerique: true,
      rendu: () => null,
      texte: (l) => formaterFcfa(l.ecart_cumule),
    },
    { cle: 'creances', entete: 'Créances', numerique: true, rendu: () => null, texte: (l) => formaterFcfa(l.encours_client) },
    ...(avecStock
      ? [
          {
            cle: 'stock',
            entete: 'Valeur du stock',
            numerique: true,
            rendu: () => null,
            texte: (l: LigneResultat) => (l.valeur_stock === undefined ? '—' : formaterFcfa(l.valeur_stock)),
          },
        ]
      : []),
  ];

  return (
    <>
      <Entete
        titre="Résultats"
        sous="Une ligne par boutique : comparer, pas additionner."
        actions={
          <ChampsPeriode
            depuis={depuis}
            jusquA={jusquA}
            onDepuis={setDepuis}
            onJusquA={setJusquA}
          />
        }
      />

      <Erreur erreur={error} />

      <Carte>
        {isPending ? (
          <Chargement />
        ) : lignes.length === 0 ? (
          <Vide texte="Aucune boutique dans votre portée." />
        ) : (
          <>
            <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
              <BoutonImprimer
                onClick={() => {
                  ouvrir(() =>
                    exporterTableauPdf({
                      titre: 'Résultats',
                      sousTitre: sousTitrePeriode(depuis, jusquA),
                      nomFichier: 'resultats',
                      colonnes: colonnesPdf,
                      lignes,
                    }),
                  );
                }}
              />
            </div>
            <div className="tableau-defilant">
            <table>
              <thead>
                <tr>
                  <th>Boutique</th>
                  <th>Ventes</th>
                  <th>Chiffre d’affaires</th>
                  <th>Panier moyen</th>
                  {avecMarge && (
                    <>
                      <th>Marge</th>
                      <th>Taux</th>
                    </>
                  )}
                  <th>Annulations</th>
                  <th>Écart de caisse</th>
                  <th>Créances</th>
                  {avecStock && <th>Valeur du stock</th>}
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => (
                  <LigneBoutique
                    key={ligne.boutique_id}
                    ligne={ligne}
                    avecMarge={avecMarge}
                    avecStock={avecStock}
                  />
                ))}
              </tbody>
              {lignes.length > 1 && (
                <tfoot>
                  <Total lignes={lignes} avecMarge={avecMarge} avecStock={avecStock} />
                </tfoot>
              )}
            </table>
            </div>
          </>
        )}
      </Carte>
    </>
  );
}

/**
 * Résultats détaillé — les ventes de chaque boutique, article par article.
 *
 * Le résultat global dit laquelle marche ; celui-ci dit AVEC QUOI. Deux
 * informations que le premier onglet ne donne pas viennent aider la
 * décision : la part de chaque article dans le chiffre de sa boutique — ce
 * qui signale une dépendance à un seul article — et son prix de vente moyen,
 * qui bouge quand les remises de ligne s'accumulent sans que personne ne
 * l'ait décidé.
 *
 * Les articles sont triés par chiffre d'affaires décroissant : c'est la
 * question qu'on pose à cet écran — qu'est-ce qui vend le plus — pas l'ordre
 * alphabétique du catalogue.
 */
export function ResultatsDetaille(): ReactNode {
  const { boutiqueId } = useSession();
  const [depuis, setDepuis] = useState(debutDuMois);
  const [jusquA, setJusquA] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isPending, error } = useQuery({
    queryKey: ['resultats-articles', boutiqueId, depuis, jusquA],
    queryFn: () =>
      appeler<ResultatsParArticle>(
        `/resultats/articles?depuis=${depuis}&jusqu_a=${jusquA}` +
          (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`),
      ),
  });

  return (
    <>
      <Entete
        titre="Résultats détaillé"
        sous="Les ventes de chaque boutique, article par article."
        actions={
          <ChampsPeriode
            depuis={depuis}
            jusquA={jusquA}
            onDepuis={setDepuis}
            onJusquA={setJusquA}
          />
        }
      />

      <Erreur erreur={error} />

      {isPending ? (
        <Carte>
          <Chargement />
        </Carte>
      ) : data === undefined || data.boutiques.length === 0 ? (
        <Carte>
          <Vide texte="Aucune boutique dans votre portée." />
        </Carte>
      ) : (
        <div className="pile">
          {data.boutiques.map((boutique) => (
            <ArticlesBoutique
              key={boutique.boutique_id}
              boutique={boutique}
              sousTitre={sousTitrePeriode(depuis, jusquA)}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Une carte par boutique, avec ses articles vendus sur la période.
 *
 * Groupée par boutique et non en un seul grand tableau : c'est la question
 * posée — « quels articles font vivre CETTE boutique » — qu'un tableau
 * unique de toutes les boutiques mélangées ne laisse pas lire d'un coup
 * d'œil.
 */
function ArticlesBoutique({
  boutique,
  sousTitre,
}: {
  boutique: ResultatsParArticle['boutiques'][number];
  sousTitre: string;
}): ReactNode {
  const { ouvrir } = useApercuPdf();

  const colonnesPdf: Colonne<LigneArticleResultat>[] = [
    { cle: 'reference', entete: 'Référence', rendu: () => null, texte: (a) => a.reference },
    { cle: 'article', entete: 'Article', rendu: () => null, texte: (a) => a.designation },
    { cle: 'quantite', entete: 'Quantité vendue', numerique: true, rendu: () => null, texte: (a) => String(a.quantite) },
    {
      cle: 'prixMoyen',
      entete: 'Prix moyen',
      numerique: true,
      rendu: () => null,
      texte: (a) => formaterFcfa(a.quantite === 0 ? 0 : Math.round(a.chiffre_affaires / a.quantite)),
    },
    { cle: 'ca', entete: 'Chiffre d’affaires', numerique: true, rendu: () => null, texte: (a) => formaterFcfa(a.chiffre_affaires) },
    {
      cle: 'part',
      entete: 'Part du CA',
      numerique: true,
      rendu: () => null,
      texte: (a) =>
        `${String(
          boutique.chiffre_affaires_total === 0
            ? 0
            : Math.round((a.chiffre_affaires / boutique.chiffre_affaires_total) * 1000) / 10,
        )} %`,
    },
  ];

  return (
    <Carte
      titre={`${boutique.code} — ${boutique.nom}`}
      actions={
        boutique.articles.length === 0 ? undefined : (
          <BoutonImprimer
            onClick={() => {
              ouvrir(() =>
                exporterTableauPdf({
                  titre: `Résultats détaillé — ${boutique.code} — ${boutique.nom}`,
                  sousTitre,
                  nomFichier: `resultats-detaille-${boutique.code}`,
                  colonnes: colonnesPdf,
                  lignes: boutique.articles,
                }),
              );
            }}
          />
        )
      }
    >
      {boutique.articles.length === 0 ? (
        <Vide texte="Aucun article vendu sur cette période." />
      ) : (
        <div className="tableau-defilant">
          <table>
            <thead>
              <tr>
                <th>Référence</th>
                <th>Article</th>
                <th>Quantité vendue</th>
                <th>Prix moyen</th>
                <th>Chiffre d’affaires</th>
                <th>Part du CA</th>
              </tr>
            </thead>
            <tbody>
              {boutique.articles.map((article, index) => (
                <LigneArticle
                  key={article.produit_id}
                  article={article}
                  meilleureVente={index === 0}
                  totalBoutique={boutique.chiffre_affaires_total}
                />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td />
                <td>
                  <Nombre valeur={boutique.quantite_totale} />
                </td>
                <td />
                <td>
                  <Montant valeur={boutique.chiffre_affaires_total} />
                </td>
                <td>100 %</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Carte>
  );
}

function LigneArticle({
  article,
  meilleureVente,
  totalBoutique,
}: {
  article: LigneArticleResultat;
  /** L'article est le premier du tri par chiffre d'affaires : à signaler,
      pas à faire deviner en comparant les lignes une à une. */
  meilleureVente: boolean;
  totalBoutique: number;
}): ReactNode {
  const prixMoyen = article.quantite === 0 ? 0 : Math.round(article.chiffre_affaires / article.quantite);
  const partDuCa = totalBoutique === 0 ? 0 : Math.round((article.chiffre_affaires / totalBoutique) * 1000) / 10;

  return (
    <tr>
      <td className="mono">{article.reference}</td>
      <td>
        {article.designation}
        {meilleureVente && (
          <>
            {' '}
            <Etiquette ton="vert" texte="Meilleure vente" />
          </>
        )}
      </td>
      <td>
        <Nombre valeur={article.quantite} />
      </td>
      <td>
        <Montant valeur={prixMoyen} />
      </td>
      <td>
        <Montant valeur={article.chiffre_affaires} />
      </td>
      <td>{`${String(partDuCa)} %`}</td>
    </tr>
  );
}

function LigneBoutique({
  ligne,
  avecMarge,
  avecStock,
}: {
  ligne: LigneResultat;
  avecMarge: boolean;
  avecStock: boolean;
}): ReactNode {
  return (
    <tr>
      <td>
        <span className="mono">{ligne.code}</span> {ligne.nom}
      </td>
      <td>
        <Nombre valeur={ligne.ventes} />
      </td>
      <td>
        <Montant valeur={ligne.chiffre_affaires} />
      </td>
      <td>
        <Montant valeur={ligne.panier_moyen} />
      </td>
      {avecMarge && (
        <>
          <td>
            <Montant valeur={ligne.marge} />
          </td>
          <td>{ligne.taux_marge_pct === undefined ? '—' : `${String(ligne.taux_marge_pct)} %`}</td>
        </>
      )}
      <td>
        {/* Les annulations ne sont pas soustraites du chiffre : beaucoup de
            ventes ET beaucoup d'annulations ne se lit pas comme une boutique
            tranquille. */}
        {ligne.contrepassations === 0 ? (
          <Nombre valeur={0} />
        ) : (
          <Etiquette
            ton="ambre"
            texte={`${String(ligne.contrepassations)} · ${String(ligne.montant_contrepasse)} F`}
          />
        )}
      </td>
      <td>
        {ligne.ecart_cumule === 0 ? (
          <Nombre valeur={0} />
        ) : (
          <Etiquette ton={ligne.ecart_cumule < 0 ? 'rouge' : 'ambre'} texte={`${String(ligne.ecart_cumule)} F`} />
        )}
      </td>
      <td>
        <Montant valeur={ligne.encours_client} />
        {ligne.encours_echu > 0 && (
          <>
            {' '}
            <Etiquette ton="rouge" texte={`${String(ligne.encours_echu)} F échus`} />
          </>
        )}
      </td>
      {avecStock && (
        <td>
          <Montant valeur={ligne.valeur_stock} />
        </td>
      )}
    </tr>
  );
}

function Total({
  lignes,
  avecMarge,
  avecStock,
}: {
  lignes: LigneResultat[];
  avecMarge: boolean;
  avecStock: boolean;
}): ReactNode {
  const somme = (extrait: (ligne: LigneResultat) => number): number =>
    lignes.reduce((total, ligne) => total + extrait(ligne), 0);

  const ventes = somme((ligne) => ligne.ventes);
  const chiffre = somme((ligne) => ligne.chiffre_affaires);

  return (
    <tr>
      <th scope="row">Total</th>
      <td>
        <Nombre valeur={ventes} />
      </td>
      <td>
        <Montant valeur={chiffre} />
      </td>
      <td>
        <Montant valeur={ventes === 0 ? 0 : Math.round(chiffre / ventes)} />
      </td>
      {avecMarge && (
        <>
          <td>
            <Montant valeur={somme((ligne) => ligne.marge ?? 0)} />
          </td>
          <td />
        </>
      )}
      <td>
        <Nombre valeur={somme((ligne) => ligne.contrepassations)} />
      </td>
      <td>
        <Nombre valeur={somme((ligne) => ligne.ecart_cumule)} />
      </td>
      <td>
        <Montant valeur={somme((ligne) => ligne.encours_client)} />
      </td>
      {avecStock && (
        <td>
          <Montant valeur={somme((ligne) => ligne.valeur_stock ?? 0)} />
        </td>
      )}
    </tr>
  );
}
